<template>
  <MenuButton :full-width="fullWidth" mobile-sheet sheet-title="Switch portfolio">
    <template #menu-button-trigger>
      <slot name="trigger" />
    </template>

    <MenuButtonItem
      v-for="option in options"
      :key="option.key"
      :title="option.label"
      :subtitle="option.count"
      :is-selected="option.key === model"
      @click="model = option.key"
    />

    <!-- Nothing to switch between yet: say what a portfolio is for, not a blank list -->
    <View v-if="!portfolios.length" direction="column" gap="xs" class="empty">
      <Text variant="label-medium">No portfolios yet</Text>
      <Text variant="body-small" color="secondary">{{ emptyHint }}</Text>
    </View>

    <template #menu-footer>
      <MenuButtonItem v-if="portfolios.length" title="Manage portfolios" icon="settings-01" @click="emit('manage')" />
      <MenuButtonItem v-else title="Create a portfolio" icon="plus" @click="emit('create')" />
    </template>
  </MenuButton>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { MenuButton, MenuButtonItem, Text, View } from '@tallo/design-system';
import { PortfolioDto } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  Holdings,
  PortfolioFilter,
  allPortfoliosFilter,
  holdingsLabel,
  noHoldings,
  unassignedPortfolioFilter,
} from '@tallo/investor/property';

const props = defineProps<{
  portfolios: PortfolioDto[];
  /** What each entry holds, keyed like the filter: a portfolio id or `unassigned` */
  counts: Record<string, Holdings>;
  total: Holdings;
  fullWidth?: boolean;
}>();

/** Which portfolio the page is showing */
const model = defineModel<PortfolioFilter>({ required: true });
const emit = defineEmits<{ manage: []; create: [] }>();

const { isAdmin } = storeToRefs(useInvestorStore());

const options = computed(() => {
  if (!props.portfolios.length) {
    return [];
  }

  return [
    { key: allPortfoliosFilter, label: 'All properties', count: holdingsLabel(props.total) },
    ...props.portfolios.map((portfolio) => ({
      key: portfolio.id,
      label: portfolio.name,
      count: holdingsLabel(props.counts[portfolio.id] ?? noHoldings),
    })),
    {
      key: unassignedPortfolioFilter,
      label: 'Unassigned',
      count: holdingsLabel(props.counts[unassignedPortfolioFilter] ?? noHoldings),
    },
  ];
});

// An admin groups properties for the team; a member groups the ones they run, into a portfolio of their own
const emptyHint = computed(() =>
  isAdmin.value
    ? 'Group properties into a portfolio and hand it to a teammate, who then runs everything inside.'
    : 'Group the properties you run into a portfolio of your own. An admin can also hand you one to run.',
);
</script>

<style scoped lang="scss">
.empty {
  max-width: 18rem;
  padding: var(--t-spacing-sm) var(--t-spacing-md);
}
</style>
